import { getErrorMessage } from '@givebacks/utils';
import axios, { AxiosError, AxiosInstance, CreateAxiosDefaults } from 'axios';

import { Refresher } from './refresher';
import {
  BetterApiRefreshResponseError,
  IBetterApiClient,
  IBetterApiClientConstructor,
  THeaders,
} from './types';

class BetterApiClient<
  TRefreshResponse extends Record<string, unknown> | BetterApiRefreshResponseError =
    | Record<string, unknown>
    | BetterApiRefreshResponseError,
  TServices = Record<string, string>,
> implements IBetterApiClient<TRefreshResponse, TServices>
{
  api: AxiosInstance;
  apiWithoutInterceptors: AxiosInstance;
  getRefreshToken: () => Promise<TRefreshResponse>;
  service: Record<keyof TServices, AxiosInstance>;

  private _defaultHeaders: THeaders | undefined;
  private _authHeaders: THeaders | undefined;
  private createAuthHeaders?: (response: TRefreshResponse) => THeaders;
  private refresher: Refresher<TRefreshResponse>;

  /**
   * Subscribers for the refresh token
   * */
  private onRefreshTokenSubscribers: Set<
    (response: TRefreshResponse | BetterApiRefreshResponseError) => void
  > = new Set();

  constructor({
    baseURL,
    getRefreshToken,
    createAuthHeaders,
    defaultHeaders,
    apiServices,
  }: IBetterApiClientConstructor<TServices, TRefreshResponse>) {
    this.api = this.createApiInstance(baseURL, defaultHeaders);
    this.apiWithoutInterceptors = this.createApiInstance(baseURL, defaultHeaders);
    this.defaultHeaders = defaultHeaders;
    this.service = this.createApiServices(apiServices || {});

    this.getRefreshToken = getRefreshToken;
    this.createAuthHeaders = createAuthHeaders;

    this.refresher = new Refresher<TRefreshResponse>({
      getRefreshToken,
    });
    this.setSessionInterceptor(this.api);
  }

  onRefreshToken(callback: (response: TRefreshResponse | BetterApiRefreshResponseError) => void) {
    this.onRefreshTokenSubscribers.add(callback);
    return () => this.onRefreshTokenSubscribers.delete(callback);
  }

  setAuthHeaders(response: TRefreshResponse) {
    const newAuthHeaders = this.createAuthHeaders?.(response);
    // Set the auth headers on the instance so we don't lose them when we set the default headers
    this._authHeaders = newAuthHeaders;
    this.api.defaults.headers.common = {
      ...newAuthHeaders,
      ...this._defaultHeaders,
    };
  }

  get defaultHeaders() {
    return this._defaultHeaders;
  }

  set defaultHeaders(headers: THeaders | undefined) {
    if (!headers) {
      this._defaultHeaders = undefined;
      return;
    }
    const authHeaders = this._authHeaders || {};

    this._defaultHeaders = headers;
    this.api.defaults.headers.common = { ...authHeaders, ...headers };
  }

  private notifyRefreshTokenSubscribers(
    response: TRefreshResponse | BetterApiRefreshResponseError
  ) {
    this.onRefreshTokenSubscribers.forEach((callback) => callback(response));
  }

  async refreshToken() {
    try {
      // Get the new session or await for the queue to be processed
      const refreshResponse = await this.refresher.refresh({
        onRefreshToken: this.notifyRefreshTokenSubscribers.bind(this),
      });

      if ('error' in refreshResponse) {
        throw Error(refreshResponse.error as string);
      }

      this.setAuthHeaders(refreshResponse);
      void this.refresher.processQueue(false, refreshResponse);

      return refreshResponse;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const errorMessage = getErrorMessage(error);
      // Process the queue of promises with an error
      void this.refresher.processQueue(!!error, null);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      return { error: errorMessage };
    }
  }

  /**
   * Set the session interceptor for the api client
   */
  private setSessionInterceptor(axiosInstance: AxiosInstance) {
    const interceptor = axiosInstance.interceptors.response.use(
      function onFulfilled(response) {
        return response;
      },
      async (error: AxiosError) => {
        try {
          // Last config
          const prevRequest = error.config;
          if (!prevRequest) return Promise.reject(error);

          // If the request is already sent, reject the promise
          if ('sent' in prevRequest && prevRequest.sent) return Promise.reject(error);

          const isRequestUnauthorized = error?.response?.status === 401;

          if (isRequestUnauthorized) {
            // Set the request as sent

            Object.assign(prevRequest, { sent: true });
            //
            try {
              // Get the new session or await for the queue to be processed
              const refreshResponse = await this.refreshToken();

              if (!refreshResponse || 'error' in refreshResponse) {
                return Promise.reject(error);
              }
              // Set the previous request headers and return the api client with the previous request
              const headers = this.createAuthHeaders?.(refreshResponse) || ({} as THeaders);

              Object.assign(prevRequest.headers, headers);

              return axiosInstance.request(prevRequest);
            } catch (error: unknown) {
              return Promise.reject(error as Error);
            }
          }
        } catch (error) {
          return Promise.reject(error as Error);
        }

        return Promise.reject(error);
      }
    );
    return interceptor;
  }

  /** Creates an axios instance with the base URL and default headers */
  private createApiInstance(baseURL: string, defaultHeaders: THeaders | undefined) {
    const axiosConfig: CreateAxiosDefaults = {
      baseURL,
      headers: defaultHeaders,
      withCredentials: true,
    };
    if (import.meta.env.NODE_ENV === 'test') {
      axiosConfig.adapter = 'http';
    }

    return axios.create(axiosConfig);
  }

  private createApiServices(services?: Record<string, string>) {
    if (!services) {
      return {} as Record<keyof TServices, AxiosInstance>;
    }

    const apiServices = {} as Record<keyof TServices, AxiosInstance>;
    Object.entries(services).forEach(([serviceName, baseURL]) => {
      const apiInstance = this.createApiInstance(baseURL, this.defaultHeaders);
      this.setSessionInterceptor(apiInstance);
      apiServices[serviceName] = apiInstance;
    });
    return apiServices;
  }
}

export { BetterApiClient };
